import type { ColDef, RowClickedEvent } from 'ag-grid-community';
import { capitalCase } from 'case-anything';
import { type FC, useEffect, useState } from 'react';
import { Button, OverlayTrigger, Popover } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import EditblackIcon from '../../../assets/edit-icon.svg';
import VerticalDotMenuIcon from '../../../assets/vertical-dot-menu.svg';
import { useAuth, useQueryRequest } from '../../../modules/hooks';
import type { DynamicQuestionPayload } from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';
import { buildRequestUrl } from '../../../modules/utils/buildRequestUrl';
import { AddQuestionManagerModal } from '../../Modals/PropertyQuestionsModal';
import { DataTable } from '../DataTable';

interface PropertyConfigModalProps {
  show: boolean;
  propertyId: string;
  handleClose: () => void;
}

export const paymentStatusColorMapper: Record<string, string> = {
  CONFIRMED: 'primary',
  STARTED: 'warning',
};

export const QuestionManagerModal: FC<PropertyConfigModalProps> = ({
  show,
  propertyId,
  handleClose,
}) => {
  const { credentialsInfo } = useAuth()!;
  const [dynamicQuestionsData, setDynamicQuestionsData] = useState<
    DynamicQuestionPayload[]
  >([]);
  const [questionId, setQuestionId] = useState<string>('');

  const getReservationUrl = buildRequestUrl(endpoints.DYNAMIC_QUESTIONS, {
    propertyId,
  });

  const {
    data: dynamicQuestionResult,
    loading: loadingDynamicQuestion,
    error: errorDynamicQuestion,
    refetch: getDynamicQuestions,
  } = useQueryRequest<DynamicQuestionPayload[]>(getReservationUrl, {
    authToken: credentialsInfo?.token,
  });

  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [selectedQuestion, setSelectedQuestion] = useState<
    DynamicQuestionPayload | undefined
  >(undefined);

  const onClose = () => {
    setDynamicQuestionsData([]);
    handleClose();
  };

  const onShowEditModal = (item?: DynamicQuestionPayload) => {
    if (item) {
      setSelectedQuestion(item);
      setShowEditModal(true);
      setShowModal(true);
    }
  };

  const renderMenuIcon = (item: RowClickedEvent<DynamicQuestionPayload>) => {
    return (
      <div className="menu-icon-container-style">
        <OverlayTrigger
          trigger="click"
          placement="left"
          rootClose
          overlay={
            <Popover id="popover-basic">
              <Popover.Body>
                <div
                  onClick={(e) => {
                    document.body.click();

                    onShowEditModal(item.data);
                  }}
                  style={{ paddingBottom: '5px' }}
                  className="edit-damage-report-container"
                >
                  <img
                    className="edit-icon-image-style"
                    src={EditblackIcon}
                    alt="edit-black-icon"
                  />
                  <div className="edit-text-style">Edit</div>
                </div>
              </Popover.Body>
            </Popover>
          }
        >
          <div>
            <img src={VerticalDotMenuIcon} alt="vertical-icon" />
          </div>
        </OverlayTrigger>
      </div>
    );
  };

  const showEditHandler = () => {
    setShowModal(true);
  };
  const updateTable = () => {
    getDynamicQuestions();
  };

  useEffect(() => {
    if (dynamicQuestionResult && show) {
      setDynamicQuestionsData(dynamicQuestionResult.result);
    }
  }, [dynamicQuestionResult, show]);

  const [columns] = useState<ColDef<DynamicQuestionPayload>[]>([
    {
      field: 'type',
      headerName: 'Type',
      flex: 2,
      valueFormatter: (params) =>
        capitalCase(params.value, { keepSpecialCharacters: false }),
    },
    { field: 'question', headerName: 'Message', flex: 4 },
    { field: 'isRequired', headerName: 'Required', flex: 1 },
    { field: 'isActive', headerName: 'Is active', flex: 1 },

    {
      headerName: 'ACTIONS',
      cellRenderer: renderMenuIcon,
      flex: 1,
    },
  ]);

  return (
    <Modal
      show={show}
      onHide={() => {
        handleClose();
      }}
      size="xl"
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title className="h5">Questions</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mt-16px">
          <DataTable
            showSearchInput={false}
            changeState={0}
            rowData={dynamicQuestionsData}
            columnDefs={columns}
            pagination
            component="properties"
            paginationPageSize={5}
            addButtonText="Add Question"
            addButtonFunction={showEditHandler}
            loading={loadingDynamicQuestion}
          />
        </div>
      </Modal.Body>
      <Modal.Footer style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div>
          <Button className="cancel-button" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Modal.Footer>

      <AddQuestionManagerModal
        show={showModal}
        defaultQuestion={selectedQuestion}
        handleClose={() => {
          setShowModal(false);
          setSelectedQuestion(undefined);
          setShowEditModal(false);
        }}
        updateTable={updateTable}
        propertyId={propertyId}
        isEdit={showEditModal}
      />
    </Modal>
  );
};
